import type { FeedItem, User } from '@knocklabs/client';
import { useKnockFeed } from '@knocklabs/react';
import { COMPANY_NAME, type NotificationPayload } from '@piccolohealth/pbs-common';
import { ChakraV3, Dot, useHoverDirty } from '@piccolohealth/ui';
import { DateTime } from '@piccolohealth/util';
import React from 'react';
import { FaArchive } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { UserAvatar } from '../../components/user/UserAvatar';
import { decodeHtml } from '../../utils/decodeHtml';

interface Props {
  notification: FeedItem<NotificationPayload>;
  onClose: () => void;
}

export const NotificationItem = (props: Props) => {
  const { notification, onClose } = props;

  const from = notification.actors?.[0] as User | undefined;
  const { content } = notification.data ?? {};

  const { feedClient } = useKnockFeed();
  const navigate = useNavigate();
  const ref = React.useRef(null);
  const isHovering = useHoverDirty(ref);

  const date = DateTime.fromISO(notification.inserted_at);

  // Show a relative string if younger than 24 hours, otherwise, show a  DateTime.DATETIME_SHORT
  const formattedDate =
    date.diffNow().as('hours') > -24
      ? date.toRelative()
      : date.toLocaleString(DateTime.DATETIME_SHORT);

  const onLinkClick = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (e.target instanceof HTMLAnchorElement) {
        const href = e.target.getAttribute('href');

        if (!href) {
          return;
        }

        const url = new URL(href);

        if (url && url.origin === window.location.origin) {
          const path = url.pathname + url.search + url.hash;
          e.preventDefault();
          void navigate(path, { relative: 'path' });
          onClose();
        }
      }
    },
    [navigate, onClose],
  );

  const markNotificationAsArchived = React.useCallback(() => {
    return feedClient.markAsArchived(notification);
  }, [feedClient, notification]);

  const markNotificationAsRead = React.useCallback(() => {
    feedClient.markAsRead(notification);
  }, [feedClient, notification]);

  return (
    <ChakraV3.HStack
      ref={ref}
      py={2}
      px={6}
      gap={4}
      align='center'
      bg={notification.read_at ? 'white' : 'gray.50'}
      onClick={markNotificationAsRead}
      cursor={notification.read_at ? 'default' : 'pointer'}
    >
      <UserAvatar
        name={from?.name ?? COMPANY_NAME}
        picture={from?.avatar}
        secondary={from?.email ?? undefined}
        showTooltip={false}
        size='md'
      />
      <ChakraV3.Stack gap={0}>
        <ChakraV3.HStack align='baseline'>
          <ChakraV3.Text fontSize='sm' fontWeight='semibold'>
            {from?.name}
          </ChakraV3.Text>
          <ChakraV3.Text fontSize='xs' color='secondary'>
            {formattedDate}
          </ChakraV3.Text>
        </ChakraV3.HStack>
        <ChakraV3.Box
          fontSize='sm'
          color='gray.700'
          css={{
            '& a': {
              fontWeight: 'bold',
              color: 'darkPurple',
              _hover: {
                textDecoration: 'underline',
              },
            },
          }}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: legitimate usage
          dangerouslySetInnerHTML={{
            __html: decodeHtml(content ?? ''),
          }}
          onClick={onLinkClick}
        />
      </ChakraV3.Stack>
      <ChakraV3.Center w={8} h={8} flexShrink={0}>
        {isHovering ? (
          <ChakraV3.HStack gap={1}>
            <ChakraV3.Tooltip content='Archive this notification' contentProps={{ fontSize: 'sm' }}>
              <ChakraV3.IconButton
                aria-label='Archive this notification'
                size='xs'
                color='gray.500'
                variant='ghost'
                onClick={markNotificationAsArchived}
              >
                <FaArchive />
              </ChakraV3.IconButton>
            </ChakraV3.Tooltip>
          </ChakraV3.HStack>
        ) : (
          <Dot mt={2} size='2' bg={notification.seen_at ? 'transparent' : 'green.500'} />
        )}
      </ChakraV3.Center>
    </ChakraV3.HStack>
  );
};
